import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { stores } from "../context";
import LanguageSwitcher from "./LanguageSwitcher";
import {
  Building2,
  Phone,
  BookOpen,
  Sparkles,
  X,
  Calendar,
  Wine,
  Utensils,
  ChevronDown,
  UtensilsCrossed,
  User,
  LogOut,
  Sun,
  Moon,
  LogIn,
  UserPlus,
} from "lucide-react";

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenBooking: () => void;
}

export const MobileDrawer: React.FC<MobileDrawerProps> = observer(
  ({ isOpen, onClose, onOpenBooking }) => {
    const { product, user, cart, ui } = stores;
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const location = useLocation();

    const [isMenuSubopen, setIsMenuSubopen] = useState(true);

    // En: Block body scroll when mobile drawer is open to prevent background scrolling & unwanted scroll buttons
    useEffect(() => {
      if (isOpen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
      return () => {
        document.body.style.overflow = "";
      };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSelectMenu = (menuType: "FOOD" | "BAR") => {
      product.setActiveMenu(menuType);
      product.setSelectedType(null);
      onClose();

      if (location.pathname !== "/menu") {
        navigate("/menu");
      } else {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    };

    const handleLogout = () => {
      user.logout();
      cart.clearCart();
      onClose();
      navigate("/");
    };

    const getLinkClass = ({ isActive }: { isActive: boolean }) =>
      `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-serif tracking-wider uppercase transition-all ${
        isActive
          ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 font-bold border border-amber-500/30"
          : "text-stone-700 dark:text-stone-300 hover:bg-stone-200/60 dark:hover:bg-stone-900 hover:text-amber-700 dark:hover:text-amber-300"
      }`;

    return (
      <div className="fixed inset-0 z-50 md:hidden flex justify-end">
        <div
          data-testid="mobile-drawer-backdrop"
          className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        <div
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation"
          className="relative w-full max-w-xs bg-stone-50 dark:bg-stone-950 text-stone-900 dark:text-stone-100 h-full shadow-2xl border-l border-stone-200 dark:border-stone-800 flex flex-col z-10 overflow-y-auto transition-colors duration-300"
        >
          {/* TOP HEADER: User / Auth + Language + Theme + Close */}
          <div className="p-3 border-b border-stone-200 dark:border-stone-800 bg-stone-100/80 dark:bg-stone-900/50 space-y-2.5">
            <div className="flex items-center justify-between">
              {/* Left: Language & Theme */}
              <div className="flex items-center gap-1.5">
                <LanguageSwitcher />
                <button
                  type="button"
                  onClick={() => {
                    const nextTheme = ui.theme === "dark" ? "light" : "dark";
                    ui.setTheme(nextTheme);
                  }}
                  className="p-2 rounded-lg bg-stone-200 dark:bg-stone-800 text-stone-700 dark:text-amber-400 hover:bg-stone-300 dark:hover:bg-stone-700 transition cursor-pointer"
                  aria-label="Toggle Theme"
                >
                  {ui.theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
                </button>
              </div>

              {/* Right: Close button */}
              <button
                type="button"
                aria-label="Close navigation"
                onClick={onClose}
                className="p-1.5 text-stone-500 dark:text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 rounded-lg transition-colors cursor-pointer"
              >
                <X size={20} />
              </button>
            </div>

            {/* User Account Bar at Top (Icon-based for unauth, compact profile for auth) */}
            {user.isAuth ? (
              <div className="flex items-center justify-between px-2.5 py-1.5 bg-stone-200/60 dark:bg-stone-800/60 rounded-xl">
                <div className="flex items-center gap-2 min-w-0">
                  <User size={15} className="text-amber-500 shrink-0" />
                  <span className="text-xs font-medium truncate text-stone-800 dark:text-stone-200">
                    {user.user?.firstName || "Konto"}
                  </span>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate("/profile");
                    }}
                    title="Mój profil"
                    className="p-1.5 rounded-lg text-stone-600 dark:text-stone-300 hover:bg-stone-300 dark:hover:bg-stone-700 transition cursor-pointer"
                  >
                    <User size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={handleLogout}
                    title="Wyloguj się"
                    className="p-1.5 rounded-lg text-red-500 hover:bg-red-500/10 transition cursor-pointer"
                  >
                    <LogOut size={15} />
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between px-2.5 py-1.5 bg-stone-200/60 dark:bg-stone-800/60 rounded-xl">
                <span className="text-xs font-serif uppercase tracking-wider text-stone-600 dark:text-stone-400">
                  {t("auth.account", "Konto")}
                </span>

                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      user.openLoginModal();
                    }}
                    title={t("auth.login", "Logowanie")}
                    className="p-2 rounded-lg bg-stone-300 dark:bg-stone-700 text-stone-800 dark:text-stone-200 hover:bg-stone-400 dark:hover:bg-stone-600 transition cursor-pointer"
                  >
                    <LogIn size={15} />
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate("/registration");
                    }}
                    title={t("auth.register", "Rejestracja")}
                    className="p-2 rounded-lg bg-amber-600 text-amber-100 hover:bg-amber-700 transition cursor-pointer"
                  >
                    <UserPlus size={15} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* MIDDLE: Navigation Links (Clean Restaurant / Menu Navigation) */}
          <div className="p-3 space-y-1.5 flex-1">
            <div className="space-y-1">
              <button
                type="button"
                aria-expanded={isMenuSubopen}
                aria-controls="mobile-drawer-menu-section"
                className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-serif tracking-wider uppercase cursor-pointer ${
                  location.pathname === "/menu"
                    ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 font-bold border border-amber-500/30"
                    : "text-stone-700 dark:text-stone-300 hover:bg-stone-200/60 dark:hover:bg-stone-900"
                }`}
                onClick={() => setIsMenuSubopen((prev) => !prev)}
              >
                <div className="flex items-center gap-3">
                  <UtensilsCrossed
                    size={18}
                    className="text-amber-600 dark:text-amber-500"
                  />
                  <span>{t("nav.menu")}</span>
                </div>

                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${
                    isMenuSubopen ? "rotate-180 text-amber-500" : ""
                  }`}
                />
              </button>

              {isMenuSubopen && (
                <div
                  id="mobile-drawer-menu-section"
                  className="pl-4 space-y-1 pt-1"
                >
                  <div className="grid grid-cols-2 gap-1 mb-1 p-1 bg-stone-200/50 dark:bg-stone-900/60 rounded-lg">
                    <button
                      type="button"
                      onClick={() => handleSelectMenu("FOOD")}
                      className={`flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-serif uppercase transition cursor-pointer ${
                        product.activeMenu === "FOOD" &&
                        location.pathname === "/menu"
                          ? "bg-amber-500 text-stone-950 font-bold shadow"
                          : "text-stone-600 dark:text-stone-400 hover:text-amber-500"
                      }`}
                    >
                      <Utensils size={13} />
                      <span>{t("nav.foodMenu", "Potrawy")}</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleSelectMenu("BAR")}
                      className={`flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-serif uppercase transition cursor-pointer ${
                        product.activeMenu === "BAR" &&
                        location.pathname === "/menu"
                          ? "bg-amber-500 text-stone-950 font-bold shadow"
                          : "text-stone-600 dark:text-stone-400 hover:text-amber-500"
                      }`}
                    >
                      <Wine size={13} />
                      <span>{t("nav.bar", "Alkohol")}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <NavLink
              to="/restaurant"
              onClick={onClose}
              className={getLinkClass}
            >
              <Building2 size={18} />
              <span>{t("nav.restaurant")}</span>
            </NavLink>

            <NavLink to="/kontakt" onClick={onClose} className={getLinkClass}>
              <Phone size={18} />
              <span>{t("nav.contact")}</span>
            </NavLink>

            <NavLink to="/recipes" onClick={onClose} className={getLinkClass}>
              <BookOpen size={18} />
              <span>{t("nav.recipes")}</span>
            </NavLink>

            <NavLink
              to="/suggestions"
              onClick={onClose}
              className={getLinkClass}
            >
              <Sparkles size={18} />
              <span>{t("nav.suggestions")}</span>
            </NavLink>
          </div>

          {/* BOTTOM FOOTER: Booking Action Button */}
          <div className="p-3 border-t border-stone-200 dark:border-stone-800 bg-stone-100/80 dark:bg-stone-900/50">
            <button
              type="button"
              onClick={() => {
                onClose();
                onOpenBooking();
              }}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-serif text-xs font-semibold uppercase tracking-widest text-white dark:text-amber-100 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 border border-amber-400/30 transition-all cursor-pointer shadow-md"
            >
              <Calendar size={15} />
              <span>{t("nav.book", "Rezerwacja")}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }
);

export default MobileDrawer;
